import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Card, Input, Select, Button, Table, Typography, Space, Modal } from 'antd';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { default as createApiGateway } from '../../../apiGateway';
import { TruncatedCell } from '../finances-v2/components/TruncatedCell';
import { JsonView, defaultStyles, darkStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import { useThemeContext } from '../../../app/ThemeProvider';
import { useIsMobile } from '../../../hooks/useIsMobile';

const { Option } = Select;

const shortcutHint = ' (⌘/Ctrl+Enter)';

function EventLogsPage() {
  const apiGateway = createApiGateway();
  const { effective: effectiveTheme } = useThemeContext();
  const jsonViewStyles = effectiveTheme === 'dark' ? darkStyles : defaultStyles;

  const isMobile = useIsMobile();
  const [eventLogs, setEventLogs] = useState([]);
  const [totalEventLogs, setTotalEventLogs] = useState(0);
  const [loading, setLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [dsps, setDsps] = useState([]);
  const [ssps, setSsps] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [searchValue, setSearchValue] = useState(null);
  const [eventTypeValue, setEventTypeValue] = useState(null);
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsParsed, setDetailsParsed] = useState(null);
  const [detailsRaw, setDetailsRaw] = useState('');
  const [detailsTitle, setDetailsTitle] = useState('Event Data');

  const refreshData = async (options = {}) => {
    try {
      setLoading(true);

      const offset = (options.pageNumber - 1) * options.pageSize;
      const limit = options.pageSize;

      let eventLogsResponse = await apiGateway.statistics.getEventLogs({
        offset,
        limit,
        search: options.search || '',
      });

      setTotalEventLogs(eventLogsResponse.meta.totalLogs);

      // Build a nested changes object by comparing before/after
      const buildChanges = (before, after) => {
        const result = {};
        const keys = Array.from(
          new Set([...(before ? Object.keys(before) : []), ...(after ? Object.keys(after) : [])])
        );

        keys.forEach((key) => {
          const bv = before ? before[key] : undefined;
          const av = after ? after[key] : undefined;

          const bothObjects =
            bv && av && typeof bv === 'object' && typeof av === 'object' && !Array.isArray(bv) && !Array.isArray(av);

          if (bothObjects) {
            const nested = buildChanges(bv, av);
            if (nested && Object.keys(nested).length > 0) {
              result[key] = nested;
            }
          } else if (!_.isEqual(bv, av)) {
            result[key] = { before: bv, after: av };
          }
        });

        return result;
      };

      eventLogsResponse = eventLogsResponse.data
        .map((x) => {
          const user = options.users.find((u) => u.id === x.userId);

          if (x.data) {
            x.data = JSON.parse(x.data);
          }

          // If the payload contains before/after objects, compute a concise changes object
          if (
            x?.data &&
            !Array.isArray(x.data) &&
            x.data.before &&
            x.data.after &&
            typeof x.data.before === 'object' &&
            typeof x.data.after === 'object' &&
            !Array.isArray(x.data.before) &&
            !Array.isArray(x.data.after)
          ) {
            try {
              const changes = buildChanges(x.data.before, x.data.after);
              if (changes && Object.keys(changes).length > 0) {
                x.data.changes = changes;
              }
            } catch (e) {
              // noop: if diff fails, we simply don't include changes
            }
          }

          if (x.event.toLowerCase() === 'update ssp' && x.data.id) {
            const ssp = options.ssps.find((s) => s.id === x.data.id);
            x.data.name = (ssp && ssp.name) || 'unknown ssp';
          }

          if (x.event.toLowerCase() === 'update dsp' && x.data.id) {
            const dsp = options.dsps.find((d) => d.id === x.data.id);
            x.data.name = (dsp && dsp.name) || 'unknown dsp';
          }

          if (x.event.toLowerCase() === 'update partner' && x.data.id) {
            const userMatch = options.users.find((u) => u.id === x.data.id);
            x.data.name = (userMatch && userMatch.firstName) || 'unknown partner';
          }

          if (Array.isArray(x.data) && x.data.some((d) => d.dspId)) {
            x.data = x.data.map((eventData) => {
              const dsp = options.dsps.find((d) => d.id === eventData.dspId);
              return {
                ...eventData,
                dspName: (dsp && dsp.name) || 'unknown dsp',
              };
            });
          }

          if (Array.isArray(x.data) && x.data.some((d) => d.sspId)) {
            x.data = x.data.map((eventData) => {
              const ssp = options.ssps.find((dsp) => dsp.id === eventData.sspId);
              return {
                ...eventData,
                sspName: (ssp && ssp.name) || 'unknown ssp',
              };
            });
          }

          return {
            createdAt: moment.utc(x.createdAt).format('YYYY-MM-DD HH:mm:ss'),
            event: x.event,
            data: (x.data && JSON.stringify(x.data, null, 2)) || 'n/a',
            ip: x.ip,
            userAgent: x.userAgent,
            userEmail: (user && user.emailAddress) || 'unknown user',
            userId: x.userId,
          };
        })
        .filter(_.identity);

      setEventLogs(eventLogsResponse);
    } catch (e) {
      throw e;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);

      const usersResponse = await apiGateway.users.getAll();
      const dspsResponse = await apiGateway.dsps.getAll({ includeSpend: false });
      const sspsResponse = await apiGateway.ssps.getAll({ includeSpend: false });
      const eventTypesResponse = await apiGateway.statistics.getEventTypes();

      setUsers(usersResponse);
      setDsps(dspsResponse);
      setSsps(sspsResponse);
      setEventTypes(eventTypesResponse.data);

      await refreshData({
        pageNumber: 1,
        pageSize,
        users: usersResponse,
        dsps: dspsResponse,
        ssps: sspsResponse,
      });
    })();
  }, []);

  const paginationChanged = async (page, pSize) => {
    setPageSize(pSize);
    setCurrentPage(page);

    await refreshData({
      pageNumber: page,
      pageSize: pSize,
      users,
      dsps,
      ssps,
      search: [searchValue, eventTypeValue].filter(_.identity).join(','),
    });
  };

  const search = (searchString) =>
    refreshData({
      pageNumber: 1,
      pageSize: 50,
      users,
      dsps,
      ssps,
      search: searchString,
    });

  const onSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const onEventTypeSelect = (eventType) => {
    setEventTypeValue(eventType);
  };

  const onFormSubmit = () => search([searchValue, eventTypeValue].filter(_.identity).join(','));

  // Global hotkey: Cmd/Ctrl + Enter to trigger search
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        if (!loading) {
          e.preventDefault();
          onFormSubmit();
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // Re-bind when inputs/loading change so closure has current values
  }, [loading, searchValue, eventTypeValue]);

  const openDetails = (record) => {
    setDetailsTitle(`${record.event} · ${record.createdAt}`);
    const raw = record?.data || '';
    setDetailsRaw(raw);
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      setDetailsParsed(parsed);
    } catch (e) {
      setDetailsParsed(null);
    }
    setDetailsOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        title: 'Date',
        dataIndex: 'createdAt',
        key: 'createdAt',
        defaultSortOrder: 'descend',
        sorter: (a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''),
      },
      {
        title: 'Event',
        dataIndex: 'event',
        key: 'event',
        render: (text) => <TruncatedCell text={text} maxLength={40} />,
        sorter: (a, b) => (a.event || '').localeCompare(b.event || ''),
      },
      {
        title: 'Data',
        dataIndex: 'data',
        key: 'data',
        render: (text, record) => (
          <Space size={8} wrap={false}>
            <span
              style={{
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                maxWidth: 520,
                verticalAlign: 'middle',
              }}
              title={typeof text === 'string' ? text : ''}
            >
              {text}
            </span>
            <Button size="small" type="link" onClick={() => openDetails(record)}>
              View
            </Button>
          </Space>
        ),
      },
      {
        title: 'IP',
        dataIndex: 'ip',
        key: 'ip',
        render: (text) => <TruncatedCell text={text} maxLength={24} />,
        sorter: (a, b) => (a.ip || '').localeCompare(b.ip || ''),
      },
      {
        title: 'User Agent',
        dataIndex: 'userAgent',
        key: 'userAgent',
        render: (text) => <TruncatedCell text={text} maxLength={20} />,
      },
      {
        title: 'User Email',
        dataIndex: 'userEmail',
        key: 'userEmail',
        render: (text) => <TruncatedCell text={text} maxLength={80} />,
        sorter: (a, b) => (a.userEmail || '').localeCompare(b.userEmail || ''),
      },
      {
        title: 'User ID',
        dataIndex: 'userId',
        key: 'userId',
        sorter: (a, b) => (a.userId || 0) - (b.userId || 0),
        align: 'right',
      },
    ],
    []
  );

  return (
    <div className="xchange-page">
      <Card size="small" style={{ marginBottom: 12, borderRadius: 6 }} styles={{ body: { padding: 12 } }}>
        <Space
          wrap
          align="center"
          className="xchange-filters"
          style={{ width: '100%', justifyContent: 'space-between' }}
        >
          <Space wrap>
            <div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Search by IP and userId</div>
              <Input
                placeholder="Enter IP, userId, etc."
                allowClear
                onChange={onSearchChange}
                value={searchValue}
                style={{ minWidth: 260 }}
                onPressEnter={onFormSubmit}
              />
            </div>

            <div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>Event Type</div>
              <Select
                style={{ minWidth: 200 }}
                allowClear
                onChange={onEventTypeSelect}
                value={eventTypeValue}
                showSearch
                placeholder="Select event type"
                filterOption={(input, option) => (option?.value || '').toLowerCase().includes(input.toLowerCase())}
              >
                {Array.isArray(eventTypes)
                  ? eventTypes.map((eventType) => (
                      <Option key={eventType} value={eventType}>
                        {eventType}
                      </Option>
                    ))
                  : null}
              </Select>
            </div>
          </Space>

          <Button
            type="primary"
            icon={loading ? <LoadingOutlined /> : <SearchOutlined />}
            onClick={onFormSubmit}
            loading={loading}
            title={'Search (⌘/Ctrl + Enter)'}
            style={{ minWidth: 120 }}
          >
            {loading ? 'Searching…' : `Search${isMobile ? '' : shortcutHint}`}
          </Button>
        </Space>
      </Card>

      <Table
        size="small"
        rowKey={(row, idx) => `${row.createdAt}-${row.userId}-${row.event}-${idx}`}
        dataSource={eventLogs}
        columns={columns}
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          total: totalEventLogs,
          onChange: paginationChanged,
          pageSizeOptions: [50, 100, 500],
          pageSize,
          current: currentPage,
          showSizeChanger: true,
        }}
      />

      <Modal
        open={detailsOpen}
        title={detailsTitle}
        onCancel={() => setDetailsOpen(false)}
        footer={null}
        width={800}
        styles={{ body: { maxHeight: 560, overflow: 'auto' } }}
      >
        {detailsParsed ? (
          <div style={{ fontSize: 12 }}>
            <JsonView data={detailsParsed} style={jsonViewStyles} />
          </div>
        ) : (
          <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }} copyable>
            {detailsRaw || 'n/a'}
          </Typography.Paragraph>
        )}
      </Modal>
    </div>
  );
}

export default EventLogsPage;
